import { useEffect, useReducer } from 'react';

import { isNumber } from '../utils/types';

const RESET = 'RESET';
const START = 'START';
const STOP = 'STOP';
const TICK = 'TICK';

type State = {
	remaining?: number;
	running: boolean;
	endAt: number;
};

type Action = {
	type: typeof RESET | typeof START | typeof STOP | typeof TICK;
	period?: number;
	startImmediatelyAfterReset?: boolean;
	tick?: number;
};

function countdownReducer(state: State, action: Action): State {
	switch (action.type) {
		case RESET:
			return {
				...state,
				endAt: Date.now() + (action.period || 0),
				remaining: action.period,
				running: Boolean(action.startImmediatelyAfterReset),
			};
		case START: {
			if (!isNumber(state.remaining)) {
				return state;
			}

			if (process.env.NODE_ENV === 'development') {
				console.assert(state.running === false, 'Countdown already running.');
				console.assert(
					state.remaining > 0,
					'Triggering countdown.start of already finished countdown is no-op.'
				);
			}
			const endAt = Date.now() + state.remaining;
			return state.remaining > 0 ? { ...state, endAt, running: true } : state;
		}
		case STOP:
			if (process.env.NODE_ENV === 'development') {
				console.assert(state.running === true, 'Countdown already stopped.');
			}
			return { ...state, running: false };
		case TICK: {
			if (!action.tick) {
				throw new Error('Tick must be provided with TICK state.');
			}

			const newRemaining = Math.round((state.endAt - Date.now()) / action.tick) * action.tick;
			return newRemaining <= 0
				? { ...state, remaining: 0, running: false }
				: { ...state, remaining: newRemaining };
		}
		default:
			throw new Error(
				`Unsupported action passed to countdown reducer: ${JSON.stringify(action, null, 2)}`
			);
	}
}

interface CountdownProps {
	period: number;
	startImmediately?: boolean;
	tick?: number;
	onEnd?: () => void;
}

type CountdownOutput = {
	remaining?: number;
	reset(startImmediatelyAfterReset: boolean): void;
	start(): void;
	stop(): void;
	running: boolean;
};

/**
 * Hook for running countdown, every `tick` calculate new remaining time as end datetime minus current datetime and provides it as
 * `remaining`. All amounts must be in milliseconds.
 */
function useCountdown({
	period,
	startImmediately,
	tick = 1000,
	onEnd,
}: CountdownProps): CountdownOutput {
	const [{ remaining, running }, send] = useReducer(countdownReducer, {
		endAt: Date.now() + period,
		remaining: period,
		running: Boolean(startImmediately),
	});

	useEffect(() => {
		let timerId: number | null = null;
		if (running) {
			timerId = window.setTimeout(() => send({ type: TICK, tick }), tick);
		}

		if (remaining === 0 && !running) {
			onEnd?.();
		}

		return () => {
			if (timerId) {
				window.clearTimeout(timerId);
			}
		};
	}, [remaining, running, tick, onEnd]);

	return {
		remaining,
		reset(startImmediatelyAfterReset) {
			send({ type: RESET, period, startImmediatelyAfterReset });
		},
		running,
		start() {
			send({ type: START });
		},
		stop() {
			send({ type: STOP });
		},
	};
}

export default useCountdown;
